import { Activity, Client } from '../../../pure-js/types/GrooverTypes'
import { GroupByOption } from '../../../pure-js/types/GrooverTypes'
import {
  filterPublicActivities,
  groupAndSortPublicActivities,
  formatPublicActivityPrice,
  filterByDanceStyle,
  sortPublicActivities,
  PublicActivitiesFilterParams,
  PublicActivitiesQueryParams
} from '../../../pure-js/libs/PublicActivitiesHelper'
import { getGrooverText } from '../libs/GrooverTextRepository'

export const formatPrice = formatPublicActivityPrice

export { filterByDanceStyle, filterPublicActivities, sortPublicActivities }
export type { PublicActivitiesFilterParams, PublicActivitiesQueryParams }

export const formatDateSwedish = (date: string) => {
  return new Date(date)
    .toLocaleString('sv-SE', { month: 'long', year: 'numeric' })
    .replace(/^\w/, (c) => c.toUpperCase())
}

export const getLocalizedActivityType = (type: string, client: Client) => {
  const types = {
    Other: client?.typeTranslations?.Other || getGrooverText('adminGeneralEnumsActivityTypeOther'),
    Course: client?.typeTranslations?.Course || getGrooverText('adminGeneralEnumsActivityTypeCourse'),
    Occasion: client?.typeTranslations?.Occasion || getGrooverText('adminGeneralEnumsActivityTypeOccasion'),
    Event: client?.typeTranslations?.Event || getGrooverText('adminGeneralEnumsActivityTypeEvent')
  }

  return types[type as keyof typeof types] || '-'
}

export const getLocalizedActivityTypeTranslated = (type: string, client: Client) => {
  const types = {
    Other: client?.typeTranslations?.Other || getGrooverText('adminGeneralEnumsActivityTypeOther'),
    Course: client?.typeTranslations?.Course || getGrooverText('adminGeneralEnumsActivityTypeCourse'),
    Occasion: client?.typeTranslations?.Occasion || getGrooverText('adminGeneralEnumsActivityTypeOccasion'),
    Event: client?.typeTranslations?.Event || getGrooverText('adminGeneralEnumsActivityTypeEvent')
  }

  const reverseTypeMap = Object.entries(types).reduce(
    (acc, [original, translated]) => {
      acc[translated] = original
      return acc
    },
    {} as Record<string, string>
  )

  const originalKey = reverseTypeMap[type] || type

  return { translated: type, original: originalKey }
}

const TYPE_ORDER = ['Course', 'Other', 'Occasion']

/** Minimum numeric manualSortIndex in the list, or +Infinity if none (sorts last). */
const minManualSortIndexInActivities = (activities: Activity[]): number => {
  let min = Number.POSITIVE_INFINITY
  for (const a of activities) {
    if (typeof a.manualSortIndex === 'number') {
      min = Math.min(min, a.manualSortIndex)
    }
  }
  return min
}

type PrimaryGroupEntry = [groupKey: string, activities: Activity[]]

const comparePrimaryGroupsDefault = ([keyA]: PrimaryGroupEntry, [keyB]: PrimaryGroupEntry, client: Client): number => {
  const { original: originalA, translated: typeA } = getLocalizedActivityTypeTranslated(keyA, client)
  const { original: originalB, translated: typeB } = getLocalizedActivityTypeTranslated(keyB, client)
  const indexA = TYPE_ORDER.indexOf(originalA)
  const indexB = TYPE_ORDER.indexOf(originalB)

  if (indexA === -1 && indexB === -1) {
    return typeA.localeCompare(typeB)
  }

  return indexA - indexB || typeA.localeCompare(typeB)
}

const comparePrimaryGroupsManual = (a: PrimaryGroupEntry, b: PrimaryGroupEntry): number => {
  const rankA = minManualSortIndexInActivities(a[1])
  const rankB = minManualSortIndexInActivities(b[1])
  if (rankA !== rankB) {
    return rankA - rankB
  }
  return a[0].localeCompare(b[0])
}

const sortPrimaryGroupEntries = (
  entries: PrimaryGroupEntry[],
  groupBy: GroupByOption,
  groupSorting: string,
  client: Client
): PrimaryGroupEntry[] => {
  if (groupBy === GroupByOption.StartDate) {
    if (groupSorting === 'manual') {
      return [...entries].sort(comparePrimaryGroupsManual)
    }
    return [...entries].sort(([, activitiesA], [, activitiesB]) => {
      const dateA = Math.min(...activitiesA.map((a) => new Date(a.startDate).getTime()))
      const dateB = Math.min(...activitiesB.map((b) => new Date(b.startDate).getTime()))
      return dateA - dateB
    })
  }
  if (groupSorting === 'manual') {
    return [...entries].sort(comparePrimaryGroupsManual)
  }
  return [...entries].sort((a, b) => comparePrimaryGroupsDefault(a, b, client))
}

export const sortActivities = sortPublicActivities

/**
 * Client-side grouping using localized labels from GrooverText.
 * Server SSR uses groupAndSortPublicActivities from pure-js instead.
 */
export const groupAndSortActivities = (
  activities: Activity[],
  groupBy: GroupByOption,
  groupSorting: string,
  cityName: string,
  client: Client,
  disableMonthGrouping = false
) => {
  const filteredActivities = cityName
    ? activities.filter((a) => a.city?.toLowerCase() === cityName.toLowerCase())
    : activities

  if (groupBy === GroupByOption.None) {
    const sortedActivities = sortActivities(filteredActivities, groupSorting)
    return { '': { '': sortedActivities } }
  }

  const primaryGrouped = filteredActivities.reduce((acc: { [key: string]: Activity[] }, activity) => {
    let groupKey = ''

    switch (groupBy) {
      case GroupByOption.StartDate:
        groupKey = formatDateSwedish(activity.startDate || '')
        break
      case GroupByOption.Type:
        groupKey = getLocalizedActivityType(activity.type, client)
        break
      case GroupByOption.City:
        groupKey = activity.city || 'No City'
        break
      case GroupByOption.Style:
        groupKey = activity.style || 'No Style'
        break
      case GroupByOption.Period: {
        const periodEntry = activity.periodId ? client?.periods?.find((p) => p.id === activity.periodId) : null
        groupKey = periodEntry?.periodName ?? activity.period ?? 'No Period'
        break
      }
      case GroupByOption.Instructors:
        groupKey = activity.instructorsNames || 'No Instructors'
        break
      case GroupByOption.Level:
        groupKey = activity.level || 'No Level'
        break
      case GroupByOption.Location: {
        if (activity.locationId && client?.locations) {
          const match = client.locations.find((l) => l.id === activity.locationId)
          if (match?.name) {
            groupKey = match.name
            break
          }
        }
        groupKey = activity.city || 'No Location'
        break
      }
      default:
        groupKey = 'Other'
    }

    if (!acc[groupKey]) acc[groupKey] = []
    acc[groupKey].push(activity)

    return acc
  }, {})

  if (groupBy === GroupByOption.StartDate) {
    const sortedGroups = sortPrimaryGroupEntries(Object.entries(primaryGrouped), groupBy, groupSorting, client)

    return Object.fromEntries(sortedGroups.map(([key, acts]) => [key, { [key]: sortActivities(acts, groupSorting) }]))
  }

  const sortedPrimaryGroups = sortPrimaryGroupEntries(Object.entries(primaryGrouped), groupBy, groupSorting, client)

  if (disableMonthGrouping) {
    return Object.fromEntries(
      sortedPrimaryGroups.map(([key, acts]) => [key, { [key]: sortActivities(acts, groupSorting) }])
    )
  }

  const finalGrouped: Record<string, Record<string, Activity[]>> = Object.fromEntries(
    sortedPrimaryGroups
      .map(([groupKey, acts]) => {
        const subGrouped = acts.reduce((acc: { [key: string]: Activity[] }, activity) => {
          const subGroupKey = formatDateSwedish(activity.startDate || '')

          if (!acc[subGroupKey]) acc[subGroupKey] = []
          acc[subGroupKey].push(activity)

          return acc
        }, {})

        Object.keys(subGrouped).forEach((key) => {
          subGrouped[key] = sortActivities(subGrouped[key], groupSorting)
        })

        const sortedSubGroups = Object.entries(subGrouped).sort(([, activitiesA], [, activitiesB]) => {
          const dateA = Math.min(...activitiesA.map((a) => new Date(a.startDate).getTime()))
          const dateB = Math.min(...activitiesB.map((b) => new Date(b.startDate).getTime()))

          if (dateA !== dateB) {
            return dateA - dateB
          }

          const timeA = Math.min(
            ...activitiesA.map((a) => {
              const [hours, minutes] = (a.time || '00:00').split(':').map(Number)
              return hours * 60 + minutes
            })
          )
          const timeB = Math.min(
            ...activitiesB.map((b) => {
              const [hours, minutes] = (b.time || '00:00').split(':').map(Number)
              return hours * 60 + minutes
            })
          )

          return timeA - timeB
        })

        return [groupKey, Object.fromEntries(sortedSubGroups)]
      })
      .sort((a, b) => {
        const [keyA, nestedA] = a as [string, Record<string, Activity[]>]
        const [keyB, nestedB] = b as [string, Record<string, Activity[]>]
        if (groupBy === GroupByOption.Type) return 0
        if (groupSorting === 'manual') {
          const rankA = minManualSortIndexInActivities(Object.values(nestedA).flat())
          const rankB = minManualSortIndexInActivities(Object.values(nestedB).flat())
          if (rankA !== rankB) return rankA - rankB
          return keyA.localeCompare(keyB)
        }
        const dateA = Math.min(
          ...Object.values(nestedA)
            .flat()
            .map((act) => new Date(act.startDate).getTime())
        )
        const dateB = Math.min(
          ...Object.values(nestedB)
            .flat()
            .map((act) => new Date(act.startDate).getTime())
        )
        return dateA - dateB
      })
  )

  return finalGrouped
}

export { groupAndSortPublicActivities }
export { hasPricedSelectedActivity, isActivityFree } from '../../../pure-js/libs/ActivityHelper'
